import {
    estimateInactiveReservedHeight,
    resolveMeasuredInactiveReservedHeight,
} from './answerPlaceholderHeight'
import { getRememberedAnswerHeight, rememberAnswerHeight } from './answerHeightMemory'
import { answerHeightContentSignature } from './answerHeightFingerprint'
import { mediaBucketsEnabled } from './mediaBuckets'
import {
    MIN_ROW_GEOMETRY_HEIGHT,
    resolveRetainedRowGeometry,
    resolveInitialRowGeometry,
    resolveRowGeometryFrameStyle,
    toPositiveRowGeometryNumber,
} from './rowGeometryFrame'

export const MIN_ANSWER_GEOMETRY_HEIGHT = MIN_ROW_GEOMETRY_HEIGHT

export const resolveAnswerReservedHeight = ({
    answer = null,
    estimatedWidth = 0,
    isThin = false,
    isTitle = false,
    postLens = false,
    editMode = false,
    textIsCollapsed = false,
    maxPayloadHeight = null,
    maxReservedHeight = null,
    textLineLimit = null,
    useFullContentMetadata = false,
    tweetExpanded = false,
} = {}) =>
    estimateInactiveReservedHeight({
        answer,
        estimatedWidth,
        isThin,
        isTitle,
        postLens,
        editMode,
        textIsCollapsed,
        maxPayloadHeight,
        maxReservedHeight,
        textLineLimit,
        useFullContentMetadata,
        tweetExpanded,
    })

export const resolveInitialAnswerGeometry = ({
    answerId = null,
    answer = null,
    estimatedHeight = 0,
    estimatedWidth = 0,
    isActive = false,
    isThin = false,
    isTitle = false,
    postLens = false,
    chatRun = null,
    editMode = false,
    textIsCollapsed = false,
    useContentEstimate = true,
    useRememberedHeight = true,
    tweetExpanded = false,
} = {}) => {
    // Remembered MEASURED heights are ground truth for a row this session at this width —
    // with every media frame pinned they are no longer mid-load-polluted — and they absorb
    // the residual estimate error on text-heavy rows. Without them, every placeholder<->content
    // swap at the mount-hold boundary re-exposes that error as a visible height flip.
    const rememberedHeight = useRememberedHeight
        ? resolveMeasuredInactiveReservedHeight({
              height: getRememberedAnswerHeight(answerId, {
                  width: estimatedWidth,
                  isActive,
                  isThin,
                  isTitle,
                  postLens,
                  chatRun,
                  textIsCollapsed,
                  contentSignature: answerHeightContentSignature(answer),
              }),
              isThin,
          })
        : null
    const estimatedReservedHeight = useContentEstimate
        ? estimateInactiveReservedHeight({
              answer,
              estimatedWidth,
              isThin,
              isTitle,
              postLens,
              editMode,
              textIsCollapsed,
              tweetExpanded,
          })
        : 0
    const estimatedReservedOrMeasuredHeight = resolveMeasuredInactiveReservedHeight({
        height: Math.max(
            toPositiveRowGeometryNumber(estimatedHeight) || 0,
            estimatedReservedHeight || 0,
        ),
        isThin,
    })
    return resolveInitialRowGeometry({
        rememberedHeight,
        estimatedHeight: estimatedReservedOrMeasuredHeight,
        minHeight: MIN_ANSWER_GEOMETRY_HEIGHT,
    })
}

export const resolveEstimatedAnswerRowHeight = ({
    answerId = null,
    answer = null,
    estimatedHeight = 0,
    estimatedWidth = 0,
    isActive = false,
    isThin = false,
    isTitle = false,
    postLens = false,
    chatRun = null,
    editMode = false,
    textIsCollapsed = false,
    activeRowChromeHeight = 0,
    useRememberedHeight = true,
    tweetExpanded = false,
} = {}) => {
    const geometry = resolveInitialAnswerGeometry({
        answerId: answerId ?? answer?.id,
        answer,
        estimatedHeight,
        estimatedWidth,
        isActive,
        isThin,
        isTitle,
        postLens,
        chatRun,
        editMode,
        textIsCollapsed,
        useRememberedHeight,
        tweetExpanded,
    })
    const extraChrome =
        isActive && !isThin && !isTitle && !geometry.hasRememberedHeight && !mediaBucketsEnabled()
            ? toPositiveRowGeometryNumber(activeRowChromeHeight) || 0
            : 0

    return geometry.height + extraChrome
}

export const resolveRetainedAnswerGeometry = ({
    measuredHeight = 0,
    previousHeight = 0,
    previousMeasuredHeight = 0,
    hasMeasuredHeight = false,
    preserveHeight = false,
    allowShrink = false,
    isThin = false,
    minHeight = MIN_ANSWER_GEOMETRY_HEIGHT,
} = {}) => {
    return resolveRetainedRowGeometry({
        measuredHeight,
        previousHeight,
        previousMeasuredHeight,
        hasMeasuredHeight,
        preserveHeight,
        allowShrink,
        minHeight,
        normalizeHeight: height =>
            resolveMeasuredInactiveReservedHeight({
                height,
                isThin,
            }),
    })
}

export const rememberRetainedAnswerGeometry = ({
    answerId = null,
    height = 0,
    width = 0,
    isActive = false,
    isThin = false,
    isTitle = false,
    postLens = false,
    chatRun = null,
    textIsCollapsed = false,
    contentSignature = null,
} = {}) =>
    rememberAnswerHeight(answerId, height, {
        width,
        isActive,
        isThin,
        isTitle,
        postLens,
        chatRun,
        textIsCollapsed,
        contentSignature,
    })

export const resolveAnswerContainerStyle = ({
    intrinsicHeight = MIN_ANSWER_GEOMETRY_HEIGHT,
    shouldRender = false,
} = {}) => {
    const height = Math.max(
        MIN_ANSWER_GEOMETRY_HEIGHT,
        toPositiveRowGeometryNumber(intrinsicHeight) || MIN_ANSWER_GEOMETRY_HEIGHT,
    )

    return resolveRowGeometryFrameStyle({
        intrinsicHeight: height,
        shouldRender,
        minHeight: MIN_ANSWER_GEOMETRY_HEIGHT,
        heightVariable: '--answer-intrinsic-height',
        heightVariableMode: 'placeholder',
        renderedHeightMode: 'none',
        placeholderHeightMode: 'height',
    })
}

export const resolveVirtualAnswerContainerStyle = resolveAnswerContainerStyle
